import { ColorStatus, MetricConfig } from '../types';
import { resolveBand, bandToColorStatus, bandLabel, hasUsableNorms, NormativeBand } from './normativeBands';
import { debugLog } from './debugLog';

export interface MetricStatusArgs {
  /** The value as displayed (may already be multiplied by 100). */
  value: number;
  /** The value in CareDaily's own scale - what normative bands are expressed in. */
  rawValue: number;
  config: MetricConfig;
  /** The metric's `trends_metadata` entry, which may carry a `normalized` block. */
  metadata?: any;
  /** CareDaily's own verdict, when they published one for this reading. */
  normativeInterpretation?: string;
}

export interface MetricStatus {
  colorStatus: ColorStatus;
  statusLabel?: string;
  source: 'norms' | 'thresholds';
}

const VALID_BANDS: NormativeBand[] = ['below_average', 'normal', 'above_average'];

/** The `careDailyValueKey` sub-keys whose numbers share the norm bands' raw scale. */
const NORMS_VALUE_KEYS = ['value', 'avg'];

/**
 * The numeric value to run the admin threshold comparisons against, mirroring
 * exactly what the old `getColorStatus` call site in `Dashboard.tsx` computed.
 *
 * This is deliberately NOT `displayValue` - on the non-`displayAs0to100`
 * branch, `displayValue` can be a formatted display STRING (e.g. "150 steps"),
 * and every threshold comparison against a string coerces to NaN, silently
 * sending a healthy resident's card to red. Pulling this into its own
 * function makes the call-site expression itself testable, not just
 * `resolveMetricStatus`'s internals with clean numbers handed to it.
 */
export function resolveThresholdValue(
  metricData: { value: unknown },
  config: Pick<MetricConfig, 'displayAs0to100'>
): number {
  if (config.displayAs0to100) {
    return typeof metricData.value === 'number'
      ? metricData.value * 100
      : parseFloat(String(metricData.value)) * 100;
  }
  // `metricData.value` is `unknown` here because the raw CareDaily payload is loosely
  // typed, but in practice it is always the numeric reading (never the formatted
  // display string - see the call site's comment). `Number(...)` makes the declared
  // `number` return type actually true at runtime (NaN, not a stray string, for
  // anything unexpected) while preserving the old comparison behaviour exactly:
  // `value >= config.greenMin` already coerced a non-number the same way via ToNumber.
  return typeof metricData.value === 'number' ? metricData.value : Number(metricData.value);
}

export interface ExtractedTrendFields {
  value: any;
  display: any;
  zscore: any;
  /** The point reading (`trendData.value`) - the number `display` and the vendor verdict describe. */
  pointValue?: any;
  normativeInterpretation?: string;
  normativeBasis?: string;
}

/**
 * Extract value/display/zscore from a raw CareDaily trend object, respecting
 * per-config sub-key overrides. `trendData` is `_rawData.recentTrends[key]`,
 * which `careDailyService.mapMetricsForDashboard` already merges with the
 * timeseries-latest and highlights.now entries before it is stored - so it can
 * already carry `normative_interpretation`/`normative_basis` directly. No
 * pairing across a separate mapped-entry object is needed or should be added
 * back; that was tried in an earlier round and reverted (see git history).
 *
 * NOTE: `trends_recently` only has avg/std/n - no value/display/zscore fields.
 * Always fall back to avg ?? value so we never return undefined when the
 * configured key is absent.
 *
 * `value` picks `avg` first (a rolling average) and feeds the threshold path,
 * unchanged. But `display` is the point reading, and the vendor verdict on the
 * merged object always belongs to that point reading (mapMetricsForDashboard
 * re-stamps it from the source that won `value`). So the verdict is carried
 * whenever the merged object has one, and `pointValue` exposes the point
 * reading so the norms path scores the number the resident actually reads.
 */
export function extractTrendFields(
  trendData: any,
  valueKey?: string | null,
  displayKey?: string | null
): ExtractedTrendFields {
  const value = (valueKey ? trendData[valueKey] : undefined) ?? trendData.avg ?? trendData.value;
  const display = (displayKey ? trendData[displayKey] : undefined) ?? trendData.display ?? trendData.avg ?? trendData.value;

  return {
    value,
    display,
    zscore: trendData.zscore,
    pointValue: trendData.value,
    normativeInterpretation: trendData.normative_interpretation,
    normativeBasis: trendData.normative_basis,
  };
}

/**
 * The number CareDaily's normative bands are compared against: the point
 * reading when there is one (what `display` and the vendor verdict describe),
 * else the extracted value (an avg-only metric shows its avg). Always the raw
 * API scale - never the `displayAs0to100` value or a formatted string.
 */
export function resolveNormsValue(metricData: { value?: unknown; pointValue?: unknown }): number {
  if (typeof metricData.pointValue === 'number') return metricData.pointValue;
  return typeof metricData.value === 'number' ? metricData.value : NaN;
}

/** Moved verbatim from Dashboard.getColorStatus - do not "improve" it here. */
function thresholdColorStatus(value: number, config: MetricConfig): ColorStatus {
  if (config.tab === 'other' ||
      (config.greenMin === undefined && config.greenMax === undefined &&
       config.yellowMin === undefined && config.yellowMax === undefined)) {
    return 'green';
  }

  if (config.greenMin !== undefined && config.greenMax !== undefined &&
      value >= config.greenMin && value <= config.greenMax) {
    return 'green';
  }

  if (config.yellowMin !== undefined && config.yellowMax !== undefined &&
      value >= config.yellowMin && value <= config.yellowMax) {
    return 'yellow';
  }

  return 'red';
}

/** Moved verbatim from Dashboard.getStatusLabel. */
function thresholdStatusLabel(colorStatus: ColorStatus, config: MetricConfig): string | undefined {
  switch (colorStatus) {
    case 'green': return config.greenLabel ?? undefined;
    case 'yellow': return config.yellowLabel ?? undefined;
    case 'red': return config.redLabel ?? undefined;
    default: return undefined;
  }
}

function adminBandColors(config: MetricConfig) {
  return {
    aboveAverage: config.bandColorAboveAverage,
    normal: config.bandColorNormal,
    belowAverage: config.bandColorBelowAverage,
  };
}

/**
 * The color and text for one metric card.
 *
 * CareDaily's normative bands win when the admin has left them on AND this
 * resident actually has a band for their age and sex. Everything else falls
 * back to the admin thresholds, unchanged. Three of CareDaily's twelve
 * assessment metrics publish no norms at all, so the fallback is a normal
 * path, not an error path.
 */
export function resolveMetricStatus(args: MetricStatusArgs): MetricStatus {
  const { value, rawValue, config, metadata, normativeInterpretation } = args;

  // Bands are in the raw reading's scale. A std or zscore sub-key is a different
  // scale, so only an unset, `value` or `avg` key may be compared with them.
  const valueKeyInBandScale = !config.careDailyValueKey || NORMS_VALUE_KEYS.includes(config.careDailyValueKey);
  const normsEnabled = config.useCareDailyNorms !== false && valueKeyInBandScale;
  const normalized = metadata?.normalized;

  if (normsEnabled && hasUsableNorms(normalized)) {
    const published = VALID_BANDS.includes(normativeInterpretation as NormativeBand)
      ? (normativeInterpretation as NormativeBand)
      : null;
    const band = published ?? resolveBand(rawValue, normalized);

    if (band) {
      return {
        colorStatus: bandToColorStatus(band, adminBandColors(config)),
        statusLabel: bandLabel(band),
        source: 'norms',
      };
    }
  }

  const colorStatus = thresholdColorStatus(value, config);
  return { colorStatus, statusLabel: thresholdStatusLabel(colorStatus, config), source: 'thresholds' };
}

/**
 * Find one metric's data in the dashboard's mapped wellness data. Moved
 * verbatim from Dashboard.getMetricDataByKey so the render loop and the
 * end-to-end test run the same lookup.
 */
export function lookupMetricData(
  wellnessData: any,
  metricKey: string,
  valueKey?: string | null,
  displayKey?: string | null
): any {
  if (!metricKey) {
    console.warn('⚠️ getMetricDataByKey called with undefined/null metricKey');
    return null;
  }

  const rawData = (wellnessData as any)?._rawData;

  // 1. Raw trend data — exact key. Always tried first so careDailyValueKey/DisplayKey take effect.
  if (rawData?.recentTrends?.[metricKey]) {
    const trendData = rawData.recentTrends[metricKey];
    const result = { ...extractTrendFields(trendData, valueKey, displayKey), metadata: rawData.metadata?.[metricKey] };
    debugLog(`✅ Found raw metric: ${metricKey}`, result);
    return result;
  }

  // 2. Raw trend data — try with trend. prefix (e.g. "wellness_score" → "trend.wellness_score")
  if (!metricKey.startsWith('trend.')) {
    const trendKey = `trend.${metricKey}`;
    if (rawData?.recentTrends?.[trendKey]) {
      const trendData = rawData.recentTrends[trendKey];
      const result = { ...extractTrendFields(trendData, valueKey, displayKey), metadata: rawData.metadata?.[trendKey] };
      debugLog(`✅ Found raw metric (trend. prefix): ${trendKey}`, result);
      return result;
    }
  }

  // 3. Direct property access — for non-CareDaily metrics (e.g. self-assessment, custom keys)
  if (wellnessData && typeof wellnessData === 'object') {
    const directValue = (wellnessData as any)[metricKey];
    if (directValue != null) {
      debugLog(`✅ Found direct metric: ${metricKey}`, directValue);
      return directValue;
    }

    // 4. CamelCase alias — legacy fallback for old configs that used camelCase keys
    const camelCaseKey = metricKey.replace('trend.', '').replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    const camelCaseValue = (wellnessData as any)[camelCaseKey];
    if (camelCaseValue != null) {
      debugLog(`✅ Found camelCase metric (legacy fallback): ${camelCaseKey}`, camelCaseValue);
      return camelCaseValue;
    }
  }

  // 5. Known-unavailable legacy keys
  switch (metricKey) {
    case 'totalMocaScore':
    case 'min2StepRank':
    case 'chairStandRank':
    case 'nutritionScore':
    case 'phq9TotalScore':
      console.warn(`⚠️ Scope metric ${metricKey} not available in CareDaily API`);
      return null;
    default:
      console.warn(`⚠️ Metric not found: ${metricKey}`);
      return null;
  }
}

export interface MetricCardDerivation {
  /** What the card prints: the x100 number, or the display string/value. */
  displayValue: any;
  status: MetricStatus;
  colorStatus: ColorStatus;
  /** Set only in single-color mode. */
  customColor?: string;
  statusLabel?: string;
  /** Shown only when norms drove the card. */
  normativeBasis?: string;
  /**
   * Colors one history bar by its own band. Set only when norms drove the card and
   * it is not custom-colored; otherwise the bars keep their threshold colors.
   * Takes the displayed point value (already x100 for `displayAs0to100`).
   */
  pointColorStatus?: (displayedValue: number) => ColorStatus | null;
}

/**
 * Everything the Dashboard render loop derives for one card from its looked-up
 * data: threshold value -> norms value -> status -> label gate -> basis gate.
 * Dashboard and the end-to-end test both call this, so a change here is a
 * change the test sees.
 */
export function deriveMetricCard(metricData: any, config: MetricConfig): MetricCardDerivation {
  // Apply x100 multiplier for metrics where API returns 0-1 but display is 0-100
  const displayValue = config.displayAs0to100
    ? (typeof metricData.value === 'number' ? metricData.value * 100 : parseFloat(String(metricData.value)) * 100)
    : (metricData.display ?? metricData.value);

  // CareDaily's normative bands are expressed in the raw API scale, so take the
  // raw number straight from the mapped metric rather than un-scaling the
  // displayed one. `displayValue` can be a formatted string (e.g. "100 steps"),
  // and dividing that by 100 yields NaN, which would silently drop a resident
  // back to threshold colors. It is the point reading the card displays, not
  // the rolling avg.
  const rawValue = resolveNormsValue(metricData);

  // The threshold path needs the same numeric value the old getColorStatus()
  // call received - NOT `displayValue`, which can be a formatted display string
  // (e.g. "150 steps") on the non-scaled branch and would silently turn every
  // threshold comparison into NaN, sending a healthy resident's card to red.
  const status = resolveMetricStatus({
    value: resolveThresholdValue(metricData, config),
    rawValue,
    config,
    metadata: metricData.metadata,
    normativeInterpretation: metricData.normativeInterpretation,
  });

  // Single-color mode overrides threshold/band color coding. The text still
  // shows the band label/basis when norms actually drove this card's status,
  // matching the spec's precedence note; a custom-color metric with no norms
  // keeps its prior suppressed-label behavior.
  const customColor = config.useCustomColor === true
    ? (config.customColor || '#6366f1')
    : undefined;
  const statusLabel = (customColor && status.source !== 'norms') ? undefined : status.statusLabel;

  // History bars must agree with the band label, so under norms each point takes
  // its own band color. Points are in the displayed scale; bands are raw.
  const normalized = metricData.metadata?.normalized;
  const pointColorStatus = status.source === 'norms' && !customColor
    ? (displayedValue: number) => {
        const band = resolveBand(config.displayAs0to100 ? displayedValue / 100 : displayedValue, normalized);
        return band ? bandToColorStatus(band, adminBandColors(config)) : null;
      }
    : undefined;

  return {
    displayValue,
    status,
    colorStatus: status.colorStatus,
    customColor,
    statusLabel,
    normativeBasis: status.source === 'norms' ? metricData.normativeBasis : undefined,
    pointColorStatus,
  };
}
